/**
 * Excursions — Semantic Connectors surface (vertical slice VS-1..VS-4).
 *
 * Round-trip work with external apps as meaning: send a Collection to a Miro
 * board (departure shows the honest fidelity manifest), let it evolve there,
 * then return it — the diff lands on the same items, drawn arrows become
 * canonical edges, spatial inferences become review proposals, and each
 * touched item shows its re-inflated home context (assemble_item).
 *
 * Route: /prioritization/settings/excursions (module-gated: semantic_connectors)
 * Spec:  docs/9-exploration/semantic-connectors/05-VERTICAL-SLICE-miro-round-trip.md
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftRight,
  ArrowRight,
  ExternalLink,
  Loader2,
  Send,
  Undo2,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { miroExcursionReadBoard } from '../../../lib/semanticConnectors/miroExcursionApi';
import { loadFieldMappingConfig, getColorRulesForEntity } from '../../../lib/fieldMappingRegistry';
import { assembleItem, type AssembledItem } from '../../../lib/assembleItemService';
import {
  listExcursions,
  setExcursionStatus,
  type ConnectorExcursion,
  type ExcursionStatus,
} from '../../../lib/semanticConnectors/excursionStore';
import {
  computeFidelityManifest,
  departForMiro,
} from '../../../lib/semanticConnectors/excursionExport';
import { buildMiroSnapshot } from '../../../lib/semanticConnectors/miroSnapshot';
import {
  mergeExcursionSnapshots,
  type ExcursionMergePlan,
} from '../../../lib/semanticConnectors/semanticExcursionMerge';
import {
  applyExcursionReturn,
  removedObjectFlags,
  type ApplyResult,
} from '../../../lib/semanticConnectors/excursionApply';
import type { AssertionBasis } from '../../../lib/semanticConnectors/snapshot';

// ── Small presentational helpers ──────────────────────────────────────────────

const STATUS_STYLE: Record<ExcursionStatus, string> = {
  open: 'bg-primary-100 text-primary-800',
  returned: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  reconciled: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  abandoned: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};

function StatusChip({ status }: { status: ExcursionStatus }) {
  return (
    <span className={`font-mono text-xs uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLE[status]}`}>
      {status}
    </span>
  );
}

/** Uncertainty made visible: basis + confidence on every inferred assertion. */
function BasisChip({ basis, confidence }: { basis: AssertionBasis; confidence: number }) {
  const direct = basis === 'explicit' || basis === 'symbol' || basis === 'visual_language';
  return (
    <span
      className={`font-mono text-xs px-1.5 py-0.5 rounded ${
        direct ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
      }`}
      title={direct ? 'Writes directly on apply' : 'Becomes a review proposal on apply'}
    >
      {basis} · {Math.round(confidence * 100)}%
    </span>
  );
}

// ── Context inflation panel (VS-4) ────────────────────────────────────────────

function ContextPanel({ itemId, collectionTitle }: { itemId: string; collectionTitle?: string }) {
  const [assembled, setAssembled] = useState<AssembledItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void assembleItem(itemId).then(({ data, error: err }) => {
      if (cancelled) return;
      setAssembled(data);
      setError(err);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [itemId]);

  if (loading) {
    return <div className="text-xs text-[var(--text-ter)] flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> assembling context…</div>;
  }
  if (error || !assembled) {
    return <div className="text-xs text-[var(--text-ter)]">Context unavailable{error ? ` — ${error}` : ''}.</div>;
  }

  const edgeCount = assembled.relationships?.edges?.length ?? 0;
  const groupCount = assembled.relationships?.group_memberships?.length ?? 0;
  const flags = assembled.open_flags ?? [];
  const cycle = assembled.cycle_context;

  return (
    <div className="text-xs text-[var(--text-sec)] space-y-1">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {collectionTitle && <span>Collection: <span className="text-[var(--text-pri)]">{collectionTitle}</span></span>}
        <span>Relationships: <span className="text-[var(--text-pri)]">{edgeCount}</span></span>
        <span>Groups: <span className="text-[var(--text-pri)]">{groupCount}</span></span>
        {cycle && <span>Cycle: <span className="text-[var(--text-pri)]">{String((cycle as Record<string, unknown>).cycle_name ?? (cycle as Record<string, unknown>).cycle_id ?? 'active')}</span></span>}
      </div>
      {flags.length > 0 && (
        <div className="text-[var(--mustard)]">
          {flags.length} open flag{flags.length !== 1 ? 's' : ''} re-attached to this item.
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type View =
  | { kind: 'list' }
  | { kind: 'depart' }
  | { kind: 'review'; excursion: ConnectorExcursion; plan: ExcursionMergePlan }
  | { kind: 'applied'; excursion: ConnectorExcursion; result: ApplyResult };

export default function Excursions() {
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const addNotification = useUIStore((s) => s.addNotification);
  const items = useItemsStore((s) => s.items);
  const groups = useItemRelationshipsStore((s) => s.groups);
  const groupMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const edges = useItemRelationshipsStore((s) => s.edges);

  const [excursions, setExcursions] = useState<ConnectorExcursion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: 'list' });

  // Depart form state
  const [collectionId, setCollectionId] = useState('');
  const [boardName, setBoardName] = useState('');

  // Review selection state
  const [selectedObjects, setSelectedObjects] = useState<Set<string>>(new Set());
  const [selectedRelations, setSelectedRelations] = useState<Set<number>>(new Set());

  const refresh = useCallback(async () => {
    if (!workspaceId) { setLoading(false); return; }
    const { data, error: err } = await listExcursions(workspaceId);
    setExcursions(data);
    if (err) setError(err);
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { void refresh(); }, [refresh]);

  const collections = useMemo(
    () => groups.filter((g) => g.type === 'collection' && g.status !== 'dissolved'),
    [groups],
  );

  const scope = useMemo(() => {
    if (!collectionId) return { items: [], edges: [] };
    const memberIds = new Set(
      groupMemberships.filter((m) => m.groupId === collectionId).map((m) => m.itemId),
    );
    const scopeItems = items.filter((i) => memberIds.has(i.id));
    const scopeEdges = edges.filter(
      (e) =>
        memberIds.has(e.sourceItemId) &&
        memberIds.has(e.targetItemId) &&
        (e.status === 'confirmed' || e.status === 'active'),
    );
    return { items: scopeItems, edges: scopeEdges };
  }, [collectionId, groupMemberships, items, edges]);

  const manifest = useMemo(
    () => (scope.items.length > 0 ? computeFidelityManifest(scope.items, scope.edges) : []),
    [scope],
  );

  const collectionTitle = useCallback(
    (id?: string) => (id ? collections.find((c) => c.id === id)?.title : undefined),
    [collections],
  );

  // ── Departure (VS-1) ────────────────────────────────────────────────────────

  const handleDepart = useCallback(async () => {
    if (!workspaceId || scope.items.length === 0) return;
    setBusy('depart');
    setError(null);
    const { data, error: err } = await departForMiro({
      workspaceId,
      boardName: boardName.trim() || `Excursion — ${collectionTitle(collectionId) ?? 'items'}`,
      items: scope.items,
      edges: scope.edges,
      collectionId,
      createdBy: userId ?? undefined,
    });
    setBusy(null);
    if (err || !data) {
      setError(err ?? 'Departure failed');
      return;
    }
    addNotification({
      type: 'success',
      title: 'Excursion departed',
      message: `${scope.items.length} items and ${scope.edges.length} relationships are now on the Miro board.${
        data.errors.length > 0 ? ` ${data.errors.length} object(s) failed — see the board.` : ''
      }`,
      duration: 5000,
    });
    setView({ kind: 'list' });
    setCollectionId('');
    setBoardName('');
    void refresh();
  }, [workspaceId, scope, boardName, collectionId, collectionTitle, userId, addNotification, refresh]);

  // ── Return (VS-2) ───────────────────────────────────────────────────────────

  const handleReturn = useCallback(async (excursion: ConnectorExcursion) => {
    if (!workspaceId) return;
    setBusy(excursion.id);
    setError(null);
    try {
      const read = await miroExcursionReadBoard(workspaceId, excursion.externalContainerId);
      const config = await loadFieldMappingConfig(workspaceId, 'miro');
      const snapshot = buildMiroSnapshot(
        read,
        {
          relationshipMap: config.relationshipMap,
          typeMap: config.typeMap,
          colorRules: getColorRulesForEntity(config.miroColorMaps ?? [], 'items'),
        },
        { scopeFrameId: excursion.scope.scopeFrameId },
      );
      const plan = mergeExcursionSnapshots(excursion.baseline, snapshot);
      if (excursion.status === 'open') void setExcursionStatus(excursion.id, 'returned');
      // Default selection: everything reviewable is pre-checked; removals never.
      setSelectedObjects(
        new Set(
          plan.objects
            .filter((o) => o.externalId && (o.status === 'new' || o.status === 'renamed' || o.status === 'edited'))
            .map((o) => o.externalId as string),
        ),
      );
      setSelectedRelations(
        new Set(
          plan.relations
            .map((r, index) => ({ r, index }))
            .filter(({ r }) => (r.status === 'new' || r.status === 'retyped') && r.applicable)
            .map(({ index }) => index),
        ),
      );
      setView({ kind: 'review', excursion: { ...excursion, status: 'returned' }, plan });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Board read failed');
    } finally {
      setBusy(null);
    }
  }, [workspaceId]);

  // ── Reconcile (VS-3) ────────────────────────────────────────────────────────

  const handleApply = useCallback(async () => {
    if (view.kind !== 'review') return;
    setBusy('apply');
    const result = await applyExcursionReturn(view.excursion, view.plan, {
      objectExternalIds: selectedObjects,
      relationIndexes: selectedRelations,
    }, { userId: userId ?? undefined });
    setBusy(null);
    addNotification({
      type: result.errors.length > 0 ? 'warning' : 'success',
      title: 'Excursion reconciled',
      message: `${result.itemsCreated} created, ${result.itemsUpdated} updated, ${result.edgesCreated + result.edgesRetyped} edges, ${result.proposalsCreated} proposals.`,
      duration: 5000,
    });
    setView({ kind: 'applied', excursion: view.excursion, result });
    void refresh();
  }, [view, selectedObjects, selectedRelations, userId, addNotification, refresh]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!workspaceId) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<ArrowLeftRight className="h-4 w-4 text-primary-700" />}
          iconBg="bg-primary-100"
          title="Excursions"
          subtitle="Round-trip work with external apps — meaning preserved, context re-inflated"
        />
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 text-sm text-[var(--text-ter)]">
          Sign in to a workspace to use excursions.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ArrowLeftRight className="h-4 w-4 text-primary-700" />}
        iconBg="bg-primary-100"
        title="Excursions"
        subtitle="Send a collection to Miro, let it evolve, and bring the meaning home — not a naked import"
      />

      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4 text-sm text-[var(--coral)]">{error}</div>
      )}

      {view.kind === 'list' && (
        <>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="font-heading font-semibold text-[var(--text-pri)]">Send work out</h2>
                <p className="text-sm text-[var(--text-sec)] mt-1">
                  A departure exports a collection's items and relationships to a new Miro board and
                  records the baseline it left as — with an honest note of what the board can't carry.
                </p>
              </div>
              <button
                onClick={() => setView({ kind: 'depart' })}
                className="shrink-0 inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm"
              >
                <Send className="h-4 w-4" /> New excursion
              </button>
            </div>
          </div>

          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
            <h2 className="font-heading font-semibold text-[var(--text-pri)] mb-4">Excursion history</h2>
            {loading ? (
              <div className="text-sm text-[var(--text-ter)] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : excursions.length === 0 ? (
              <p className="text-sm text-[var(--text-ter)]">No excursions yet. Work that leaves home shows up here — and comes back.</p>
            ) : (
              <ul className="divide-y divide-[var(--rule)]">
                {excursions.map((excursion) => (
                  <li key={excursion.id} className="py-3 flex items-center gap-3">
                    <StatusChip status={excursion.status} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[var(--text-pri)] truncate">
                        {collectionTitle(excursion.scope.collectionId) ?? `${excursion.scope.itemIds.length} items`} → Miro
                      </div>
                      <div className="font-mono text-xs text-[var(--text-ter)]">
                        {excursion.scope.itemIds.length} items · departed {excursion.exportedAt ? new Date(excursion.exportedAt).toLocaleDateString() : '—'}
                        {excursion.returnedAt ? ` · returned ${new Date(excursion.returnedAt).toLocaleDateString()}` : ''}
                      </div>
                    </div>
                    {excursion.externalContainerUrl && (
                      <a
                        href={excursion.externalContainerUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 hover:text-primary-700"
                        title="Open the Miro board"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                    {(excursion.status === 'open' || excursion.status === 'returned') && (
                      <button
                        onClick={() => void handleReturn(excursion)}
                        disabled={busy === excursion.id}
                        className="inline-flex items-center gap-1.5 border border-[var(--rule)] hover:border-primary-300 text-[var(--text-sec)] px-3 py-1.5 text-sm"
                      >
                        {busy === excursion.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                        Return &amp; review
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}

      {view.kind === 'depart' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-4">
          <h2 className="font-heading font-semibold text-[var(--text-pri)]">New excursion to Miro</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="font-mono text-xs uppercase tracking-wider text-[var(--text-ter)]">Collection</span>
              <select
                value={collectionId}
                onChange={(e) => setCollectionId(e.target.value)}
                className="mt-1 w-full border border-[var(--rule)] px-3 py-2 text-sm"
              >
                <option value="">Choose a collection…</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>{c.title ?? c.id}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="font-mono text-xs uppercase tracking-wider text-[var(--text-ter)]">Board name</span>
              <input
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                placeholder={collectionTitle(collectionId) ? `Excursion — ${collectionTitle(collectionId)}` : 'Board name'}
                className="mt-1 w-full border border-[var(--rule)] px-3 py-2 text-sm"
              />
            </label>
          </div>

          {collectionId && (
            <div className="text-sm text-[var(--text-sec)]">
              Departing: <span className="text-[var(--text-pri)]">{scope.items.length} items</span> and{' '}
              <span className="text-[var(--text-pri)]">{scope.edges.length} relationships</span>.
            </div>
          )}

          {manifest.length > 0 && (
            <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4">
              <div className="flex items-center gap-2 text-[var(--mustard)] text-sm font-heading font-semibold">
                <AlertTriangle className="h-4 w-4" /> What this board can't carry
              </div>
              <ul className="mt-2 space-y-1 text-sm text-[var(--mustard)]">
                {manifest.map((entry, i) => (
                  <li key={i}>
                    <span className="font-mono text-xs uppercase tracking-wider mr-2">{entry.plane}</span>
                    {entry.note}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={() => void handleDepart()}
              disabled={!collectionId || scope.items.length === 0 || busy === 'depart'}
              className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 text-sm"
            >
              {busy === 'depart' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send to Miro
            </button>
            <button onClick={() => setView({ kind: 'list' })} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              Cancel
            </button>
          </div>
        </div>
      )}

      {view.kind === 'review' && (
        <ReviewView
          excursion={view.excursion}
          plan={view.plan}
          collectionTitle={collectionTitle(view.excursion.scope.collectionId)}
          selectedObjects={selectedObjects}
          setSelectedObjects={setSelectedObjects}
          selectedRelations={selectedRelations}
          setSelectedRelations={setSelectedRelations}
          busy={busy === 'apply'}
          onApply={() => void handleApply()}
          onCancel={() => setView({ kind: 'list' })}
        />
      )}

      {view.kind === 'applied' && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-3">
          <h2 className="font-heading font-semibold text-[var(--text-pri)] flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[var(--green)]" /> Home again
          </h2>
          <p className="text-sm text-[var(--text-sec)]">
            {view.result.itemsCreated} item(s) created · {view.result.itemsUpdated} updated ·{' '}
            {view.result.edgesCreated} edge(s) drawn · {view.result.edgesRetyped} retyped ·{' '}
            {view.result.proposalsCreated} proposal(s) sent for review.
          </p>
          {view.result.skipped.length > 0 && (
            <ul className="text-xs text-[var(--text-ter)] list-disc pl-5 space-y-0.5">
              {view.result.skipped.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
          {view.result.errors.length > 0 && (
            <ul className="text-xs text-[var(--coral)] list-disc pl-5 space-y-0.5">
              {view.result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          <button onClick={() => setView({ kind: 'list' })} className="text-sm text-primary-600 hover:text-primary-700">
            Back to excursions
          </button>
        </div>
      )}
    </div>
  );
}

// ── Return review (VS-3 + VS-4) ───────────────────────────────────────────────

function ReviewView({
  excursion,
  plan,
  collectionTitle,
  selectedObjects,
  setSelectedObjects,
  selectedRelations,
  setSelectedRelations,
  busy,
  onApply,
  onCancel,
}: {
  excursion: ConnectorExcursion;
  plan: ExcursionMergePlan;
  collectionTitle?: string;
  selectedObjects: Set<string>;
  setSelectedObjects: (s: Set<string>) => void;
  selectedRelations: Set<number>;
  setSelectedRelations: (s: Set<number>) => void;
  busy: boolean;
  onApply: () => void;
  onCancel: () => void;
}) {
  const toggleObject = (externalId: string) => {
    const next = new Set(selectedObjects);
    if (next.has(externalId)) next.delete(externalId);
    else next.add(externalId);
    setSelectedObjects(next);
  };
  const toggleRelation = (index: number) => {
    const next = new Set(selectedRelations);
    if (next.has(index)) next.delete(index);
    else next.add(index);
    setSelectedRelations(next);
  };

  const reviewableObjects = plan.objects.filter(
    (o) => o.status === 'new' || o.status === 'renamed' || o.status === 'edited',
  );
  const reviewableRelations = plan.relations
    .map((relation, index) => ({ relation, index }))
    .filter(({ relation }) => relation.status === 'new' || relation.status === 'retyped');
  const removed = removedObjectFlags(plan);
  const touchedWithContext = plan.objects.filter(
    (o) => o.internalId && o.status !== 'unchanged' && o.status !== 'removed',
  );

  return (
    <div className="space-y-6">
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
        <h2 className="font-heading font-semibold text-[var(--text-pri)]">Return review</h2>
        <p className="font-mono text-xs text-[var(--text-ter)] mt-1">
          {plan.summary.unchanged} unchanged · {plan.summary.renamed} renamed · {plan.summary.edited} edited ·{' '}
          {plan.summary.moved} moved · {plan.summary.added} born on the board · {plan.summary.removed} removed there ·{' '}
          {plan.summary.relationsNew} new relations · {plan.summary.relationsRetyped} retyped · {plan.summary.relationsRemoved} erased
        </p>
      </div>

      {reviewableObjects.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-3">Items</h3>
          <ul className="space-y-2">
            {reviewableObjects.map((object) => (
              <li key={object.externalId} className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={object.externalId ? selectedObjects.has(object.externalId) : false}
                  onChange={() => object.externalId && toggleObject(object.externalId)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--text-pri)]">
                    <span className="font-mono text-xs uppercase tracking-wider text-[var(--text-ter)] mr-2">{object.status}</span>
                    {object.current?.content.text}
                    {object.previousTitle && (
                      <span className="text-[var(--text-ter)]"> (was “{object.previousTitle}”)</span>
                    )}
                  </div>
                  {object.status === 'new' && object.current?.asserted.itemCategory && (
                    <div className="mt-0.5">
                      <BasisChip
                        basis={object.current.asserted.itemCategory.basis}
                        confidence={object.current.asserted.itemCategory.confidence}
                      />
                      <span className="text-xs text-[var(--text-ter)] ml-2">category: {String(object.current.asserted.itemCategory.value)}</span>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {reviewableRelations.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-3">Relationships</h3>
          <ul className="space-y-2">
            {reviewableRelations.map(({ relation, index }) => (
              <li key={index} className="flex items-start gap-3">
                <input
                  type="checkbox"
                  disabled={!relation.applicable}
                  checked={selectedRelations.has(index)}
                  onChange={() => toggleRelation(index)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0 text-sm text-[var(--text-pri)]">
                  <span className="font-mono text-xs uppercase tracking-wider text-[var(--text-ter)] mr-2">{relation.status}</span>
                  {relation.relation?.canonicalType}
                  {relation.previousType && <span className="text-[var(--text-ter)]"> (was {relation.previousType})</span>}
                  {relation.relation?.cue && <span className="text-[var(--text-ter)]"> — {relation.relation.cue}</span>}{' '}
                  {relation.relation && (
                    <BasisChip basis={relation.relation.assertion.basis} confidence={relation.relation.assertion.confidence} />
                  )}
                  {!relation.applicable && (
                    <span className="text-xs text-[var(--text-ter)] ml-2">observation only — no platform item on one endpoint</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {removed.length > 0 && (
        <div className="bg-[var(--mustard-dim)] border border-[var(--mustard)] p-4 text-sm text-[var(--mustard)]">
          <div className="flex items-center gap-2 font-heading font-semibold"><AlertTriangle className="h-4 w-4" /> Kept at home</div>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">{removed.map((note, i) => <li key={i}>{note}</li>)}</ul>
        </div>
      )}

      {touchedWithContext.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
          <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-1">Context re-inflated</h3>
          <p className="text-xs text-[var(--text-ter)] mb-3">
            What each returning item reconnects to at home — the part that never traveled.
          </p>
          <ul className="space-y-3">
            {touchedWithContext.map((object) => (
              <li key={object.internalId} className="border-l-2 border-primary-100 pl-3">
                <div className="text-sm text-[var(--text-pri)] mb-1">{object.current?.content.text ?? object.baseline?.content.text}</div>
                <ContextPanel itemId={object.internalId as string} collectionTitle={collectionTitle} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={onApply}
          disabled={busy || (selectedObjects.size === 0 && selectedRelations.size === 0)}
          className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white px-4 py-2 text-sm"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
          Apply selected changes
        </button>
        <button onClick={onCancel} className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">Back</button>
      </div>

      {excursion.fidelityManifest.length > 0 && (
        <p className="font-mono text-xs text-[var(--text-ter)]">
          Departure manifest: {excursion.fidelityManifest.length} declared limit(s) — anything that couldn't travel re-attaches here, not on the board.
        </p>
      )}
    </div>
  );
}
